import React, { useState } from 'react'
import styled from 'styled-components'
import { Controller, ControllerRenderProps } from 'react-hook-form'
import { Box, Checkbox, Chip, FormControl, InputLabel, MenuItem, Select, Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { AddCircle } from '@mui/icons-material'
import { useIntl } from 'react-intl'

interface Props {
  name: string
  label?: JSX.Element | string
  placeholder?: string
  control?: any
  options: SelectOption[]
  initialValue?: string | number | (string | number)[]
  required?: boolean
  disabled?: boolean
  className?: string
  fullWidth?: boolean
  syncChange?: (value: string | number) => void
  dark?: boolean
  showResetOption?: boolean
  formattedSelectedLabel?: string
  title?: string
  multiple?: boolean
  tooltipContent?: JSX.Element | string
  syncArrayChange?: (value: any[]) => void
}

const useStyles = makeStyles(() => ({
  icon: {
    fill: '#fff',
  },
}))

export default function PrivilegeSelect({
  name,
  label,
  placeholder,
  control,
  options,
  required = false,
  disabled = false,
  className,
  fullWidth = true,
  dark = false,
  showResetOption = true,
  formattedSelectedLabel,
  title,
  multiple,
  tooltipContent,
  syncArrayChange,
}: Props): JSX.Element {
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const intl = useIntl()
  const classes = useStyles()

  const getPlaceholderText = () => {
    const requiredText = intl.formatMessage({ id: 'strings.required' })

    if (placeholder && required) {
      return `${placeholder} (${requiredText})`
    } else if (placeholder) {
      return placeholder
    } else if (required) {
      return requiredText
    }
    return ''
  }

  const handleDelete = (deletedValue: number | string | undefined, field: ControllerRenderProps) => {
    if (!Array.isArray(field.value) || !multiple) {
      return
    }
    const newValue = field.value.slice().filter((v) => v !== deletedValue)
    field.onChange(newValue)
    syncArrayChange?.(newValue)
  }

  const getLabelWithValue = (field?: ControllerRenderProps) => {
    const ValueElement = dark ? ValueDark : Value
    if (Array.isArray(field?.value)) {
      const labels = field.value.map((v) => options.find((o) => o.key === v)).filter(Boolean)
      if (labels.length === 0) {
        return null
      }
      return (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            maxHeight: 120,
            overflowY: 'auto',
            scrollBehavior: 'smooth',
            paddingRight: 1,
            marginBottom: '8px',
          }}
        >
          {labels.map((label) => (
            <Chip
              sx={{ marginTop: '8px' }}
              key={label?.key}
              label={label?.label}
              size="medium"
              onMouseDown={(e) => e.stopPropagation()}
              onDelete={(e) => {
                e.stopPropagation()
                handleDelete(label?.key, field)
              }}
            />
          ))}
        </Box>
      )
    }

    const option = options.find((option: SelectOption) => option.key === field?.value)
    return option ? <ValueElement>{getLabel(option)}</ValueElement> : ''
  }

  const getLabel = (option: SelectOption) => {
    if (formattedSelectedLabel) {
      return formattedSelectedLabel
    }
    return option.label
  }

  const renderTooltip = () => {
    return (
      <Tooltip title={<TooltipContent>{tooltipContent}</TooltipContent>} placement="right">
        <InfoButton>i</InfoButton>
      </Tooltip>
    )
  }

  const renderSelect = (field?: ControllerRenderProps) => {
    return (
      <>
        <Tooltip title={title || ''} placement="top" open={showTooltip}>
          <Select
            MenuProps={{ sx: { maxHeight: '400px', marginTop: '4px' } }}
            sx={{ '& .MuiSelect-select.MuiSelect-multiple': { padding: '0px 8px 0px 8px', minHeight: '51px' } }}
            multiple={multiple}
            required={required}
            onChange={(e) => field?.onChange(e.target.value)}
            variant="outlined"
            displayEmpty
            value={field?.value ?? (multiple ? [] : '')}
            renderValue={() => {
              if (!field) {
                return <Placeholder>{getPlaceholderText()}</Placeholder>
              }
              if (multiple) {
                return getLabelWithValue(field)
              }
              return field.value !== '' ? getLabelWithValue(field) : <Placeholder>{getPlaceholderText()}</Placeholder>
            }}
            inputProps={{
              classes: {
                icon: dark ? classes.icon : '',
              },
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(false)}
            onOpen={() => setShowTooltip(false)}
          >
            {showResetOption && (
              <ResetValue value="" disabled={field?.value === ''}>
                {intl.formatMessage({ id: 'strings.reset' })}
              </ResetValue>
            )}
            {options.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {multiple && <Checkbox checked={Array.isArray(field?.value) && field.value.includes(option.key)} />}
                {option.key === 0 && <PlusIcon color="primary" />}
                {option.key === 0 ? <NewOption>{option.label}</NewOption> : option.label}
              </MenuItem>
            ))}
          </Select>
        </Tooltip>
        {/* {multiple && getLabelWithValue(field?.value)} */}
      </>
    )
  }

  const Container = dark ? InputContainerDark : InputContainer

  return (
    <Container margin="normal" fullWidth={fullWidth} required={required} disabled={disabled} className={className}>
      {label && (
        <FieldLabel shrink>
          {label}
          {multiple && renderTooltip()}
        </FieldLabel>
      )}

      {control ? (
        <Controller control={control} name={name} render={({ field }) => renderSelect(field)} />
      ) : (
        renderSelect()
      )}
    </Container>
  )
}

const InputContainer = styled(FormControl)`
  margin: 10px 0 0 0;

  & .Mui-focused {
    color: inherit;
  }
`

const InputContainerDark = styled(InputContainer)`
  color: ${(props) => props.theme.customPalette.light};
  border: 1px solid;
  border-color: ${(props) => props.theme.customPalette.light};
  border-radius: 5px;
`

const FieldLabel = styled(InputLabel)`
  padding: 0 10px;
  margin-top: -20px;
  transition: none;
  transform: none;
`

const Placeholder = styled.span`
  color: ${(props) => props.theme.customPalette.mediumGray};
`

const ResetValue = styled(MenuItem)`
  color: ${(props) => props.theme.customPalette.mediumGray};
`

const Value = styled.span``

const ValueDark = styled(Value)`
  color: ${(props) => props.theme.customPalette.light};
`

const PlusIcon = styled(AddCircle)`
  margin: 0 10px 0 0;
`

const NewOption = styled.span`
  font-weight: 500;
`

const TooltipContent = styled.div`
  padding: 10px;
  font-size: 12px;
  & div {
    margin: 5px 0;
  }
`

const InfoButton = styled.div`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  text-align: center;
  font-size: 12px;
  line-height: 12px;
  padding: 2px;
  margin-left: 6px;
  background-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.customPalette.light};
`
